import React, { ReactElement, useState } from 'react';
import { defineMessages, FormattedHTMLMessage, FormattedMessage, useIntl } from 'react-intl';
import { Button as DigButton } from 'dig-components/buttons';
import classnames from 'classnames';
import { TextInput } from 'dig-components/dist/text_fields';
import { Text } from 'dig-components/typography';
import { AccountCallbackTestResponse } from '.';

type ApiAccountCallbackSectionProps = {
  url: string,
  onTestSubmit: (testUrl: string) => Promise<AccountCallbackTestResponse>,
}

type TestReportProps = {
  data: AccountCallbackTestResponse,
  unknownError: boolean,
}

const messages = defineMessages({
  eventReceived: {
    id: '',
    description: 'when using the API and you get a callback event successful',
    defaultMessage: 'Hello API Event Received',
  },
});

/**
 * Result from callback testing.
 *
 * @param props
 * @constructor
 */
function TestReport(props: TestReportProps): ReactElement {
  const intl = useIntl();

  if (props.unknownError || props.data.error) {
    return (
      <FormattedMessage
        id=''
        description='error message when testing callback URL, informing user some error has occurred'
        defaultMessage="Oops! We couldn't test because of an error: {errorMessage}"
        values={{
          errorMessage: props.data.error,
        }} />
    );
  } else {
    const eventReceivedMessage = intl.formatMessage(messages.eventReceived);

    if (props.data.matched) {
      return (
        <p className='m-settings--callback-report--success'>
          <FormattedHTMLMessage
            id=''
            description='success message while testing event callback'
            defaultMessage='<strong>Success! </strong>{eventCallbackStr} was found in the response.'
            values={{ eventCallbackStr: eventReceivedMessage }} />
        </p>
      );
    } else {
      return (
        <p className='m-settings--callback-report--failed'>
          <FormattedHTMLMessage
            id=''
            description='success message while testing event callback'
            defaultMessage='<strong>Failure! </strong>{eventCallbackStr} was NOT found in the response.'
            values={{ eventCallbackStr: eventReceivedMessage }} />
        </p>
      );
    }
  }
}

/**
 * Component for testing API Account level callback.
 * @constructor
 */
function APIAccountCallbackSection(props: ApiAccountCallbackSectionProps): ReactElement {

  const iniResponse: AccountCallbackTestResponse = {
    error: undefined,
    matched: undefined,
    message: undefined,
  };
  const [inputValue, setInputValue] = useState(props.url);
  const [loading, setLoading] = useState(false);
  const [unknownError, setUnknownError] = useState(false);
  const [testResponse, setTestResponse] = useState(iniResponse);

  async function testCallback(evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    evt.preventDefault();

    try {
      // reset state before making a network call
      setTestResponse(iniResponse);
      setLoading(true);
      setUnknownError(false);

      const testResponse = await props.onTestSubmit(inputValue);
      setTestResponse(testResponse);

      setLoading(false);
    } catch (e) {
      setLoading(false);
      setUnknownError(true);
    }
  }

  const onChange = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(evt.target.value);
  }, []);

  return (
    <div className='m-settings--section is-api no-margin'>
      <div className='m-settings--section--header'>
        <Text size='large' isBold>
          <FormattedMessage
            id=''
            description='title, settings page, title on a section that contains a form. To input a callback url which is a full http://www.example.com '
            defaultMessage='Account callback' />
        </Text>
      </div>
      <div className='m-settings--api--account-callback'>
        <p>
          <TextInput
            data-qa-ref="account-callback-text-input"
            placeholder='http://example.com/callback'
            id=''
            autoComplete='off'
            value={inputValue ?? ''}
            name='edit_account[callback_url]'
            onChange={onChange} />
        </p>
        <DigButton
          data-qa-ref="account-callback-test-button"
          isLoading={loading}
          disabled={!inputValue || loading}
          variant='opacity'
          onClick={testCallback}
        >
          <FormattedMessage
            id=''
            defaultMessage='Test'
            description='button, setting page, when clicked it will test the call back url the customer entered' />
        </DigButton>

        { /* This button submits the form which is rendered with PHP */}
        <DigButton variant='outline' data-qa-ref="account-callback-save-button">
          <FormattedMessage
            id=''
            defaultMessage='Save'
            description='button, setting page, when clicked it will save the call back url the customer entered' />
        </DigButton>
      </div>
      {(loading || unknownError || testResponse.error || testResponse.message) && (
        <div className='m-settings--callback-report'>
          <div className='m-settings--callback-report--title'>
            {loading ? (
              <FormattedMessage
                id=''
                description='text in createAPPForm informing user that test is being processed'
                defaultMessage='Testing your callback:' />
            ) : (
              <FormattedMessage
                id=''
                description='text when testing callback URL, message is being processed'
                defaultMessage='The following message was sent to your callback URL:' />
            )}
          </div>
          <pre className='m-settings--callback-report--message'>
            {loading ? (
              <FormattedMessage
                id=''
                description='text in createAPPForm informing user that test is being processed and message will appear soon'
                defaultMessage='Posting a test message to your URL...' />
            ) : testResponse.message}
          </pre>
          <div className={classnames('m-settings--callback-report--response', {
            error: testResponse.error || unknownError,
          })}
          >
            {loading ? (
              <FormattedMessage
                id=''
                description='text in createAPPForm informing user test is being processed, loading'
                defaultMessage='Processing...' />
            ) : (
              <TestReport data={testResponse} unknownError={unknownError} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default APIAccountCallbackSection;
